/*
This file contains common functionality used for manipulating blocks that have a schema input and a series of dropdowns 
for accessing JSON objects. Currently, the blocks that use this functionality are:
- Toolbox/Lists/GetFromJSONWithSchema.jsx
- Toolbox/AssistingBlocks/RequestResponseParameter.js
*/

/* global Blockly */
/* global $ */
/* global Util */

import * as postRobot from 'post-robot';
import { looksLikeXml, xmlToJson } from '../../../../../utils/xmlUtils';

// plus and minus images used for navigating through the dropdowns
export const plusImage = "data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='28px' height='28px' viewBox='0 0 28 28' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3Eicon-add 02-SNAP%3C/title%3E%3Cg id='icon-add-02-SNAP' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cpath d='M14,2 C20.627417,2 26,7.372583 26,14 C26,20.627417 20.627417,26 14,26 C7.372583,26 2,20.627417 2,14 C2,7.372583 7.372583,2 14,2 Z M14,3 C7.92486775,3 3,7.92486775 3,14 C3,20.0751322 7.92486775,25 14,25 C20.0751322,25 25,20.0751322 25,14 C25,7.92486775 20.0751322,3 14,3 Z' id='Combined-Shape' fill='%23FFFFFF'%3E%3C/path%3E%3Crect id='Rectangle' fill='%23FFFFFF' x='8' y='13' width='12' height='2' rx='0.75'%3E%3C/rect%3E%3Crect id='Rectangle-Copy' fill='%23FFFFFF' transform='translate(14.000000, 14.000000) rotate(90.000000) translate(-14.000000, -14.000000) ' x='8' y='13' width='12' height='2' rx='0.75'%3E%3C/rect%3E%3C/g%3E%3C/svg%3E";
export const minusImage = "data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='28px' height='28px' viewBox='0 0 28 28' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3Eicon-remove 02-SNAP 2%3C/title%3E%3Cg id='icon-remove-02-SNAP' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cpath d='M14,2 C20.627417,2 26,7.372583 26,14 C26,20.627417 20.627417,26 14,26 C7.372583,26 2,20.627417 2,14 C2,7.372583 7.372583,2 14,2 Z M14,3 C7.92486775,3 3,7.92486775 3,14 C3,20.0751322 7.92486775,25 14,25 C20.0751322,25 25,20.0751322 25,14 C25,7.92486775 20.0751322,3 14,3 Z' id='Combined-Shape' fill='%23FFFFFF'%3E%3C/path%3E%3Crect id='Rectangle' fill='%23FFFFFF' x='8' y='12.5' width='12' height='3' rx='1.25'%3E%3C/rect%3E%3C/g%3E%3C/svg%3E";
export const IMAGE_WIDTH = 24;
export const IMAGE_HEIGHT = 24;

// returns an array of the dropdown fields in a given list input
export function getDropdownFields(input) {
    return input.fieldRow.filter(field => field instanceof Blockly.FieldDropdown);
}

// add the dropdown for the next level in the JSON tree
export function addDropdownKey(config) {
    const {
        input,                  // block input containing the dropdowns
        jsonStructure,          // the JSON tree structure used for the block
        selectedValue = null,   // whether to select a specific value in the newly created dropdown
        updateCallback,         // callback to update the dropdown options when the selected option for a dropdown changes
        afterRenderCallback,    // callback after rendering the changes for the block
        plusClick,              // function to call when the plus button is clicked  
        minusClick,             // function to call when the minus button is clicked
        treatNullSchemaAsUnknownType
    } = config;
        
    // no JSON schema has been input yet, or we have arrived at a leaf
    if (!jsonStructure || Object.keys(jsonStructure).length === 0) return;

    // remove the PLUS and DELETE fields if they exist
    input.removeField?.('PLUS');
    input.removeField?.('DELETE');

    const dropdownFields = getDropdownFields(input);

    // navigate through the JSON tree to the last expanded level
    let jsonStructureAtCurrentLevel = jsonStructure;
    for (let field of dropdownFields) {
        const key = field.getValue();
        jsonStructureAtCurrentLevel = jsonStructureAtCurrentLevel ? jsonStructureAtCurrentLevel[key] : undefined;
        if (!jsonStructureAtCurrentLevel) break;
    }

    if (jsonStructureAtCurrentLevel && typeof jsonStructureAtCurrentLevel === 'object' && 
        Object.keys(jsonStructureAtCurrentLevel).length > 0) {
        // create a new dropdown with the items from the current JSON object and set the selected value of the dropdown
        const options = Object.keys(jsonStructureAtCurrentLevel).map(k => [k, k]);
        const currentVal = selectedValue || options[0][0];
        const dd = new Blockly.FieldDropdown(() => options);
        dd.setValue(currentVal);

        // handle dropdown option changed
        if (updateCallback) {
            dd.setValidator(newValue => {
                if (newValue !== dd.getValue()) {
                    updateCallback(dropdownFields.length);
                }
                return newValue;
            });
        }

        // add the new dropdown to the block (names of dropdowns are 0 for the first, 1 for the second, etc.)
        input.appendField(dd, dropdownFields.length);
    }

    // add the PLUS and DELETE fields back in and set the output shape of the block
    input.appendField(new Blockly.FieldImage(plusImage, 24, 24, '*', plusClick), 'PLUS')
         .appendField(new Blockly.FieldImage(minusImage, 24, 24, '*', minusClick), 'DELETE');

    input.sourceBlock_.initSvg();
    input.sourceBlock_.render();

    setTimeout(() => {
        setOutputShapeOfBlock({
            input: input,
            jsonStructure: jsonStructure,
            treatNullSchemaAsUnknownType
        });
        afterRenderCallback?.();
    });
}

// remove the last displayed dropdown from the block
export function removeDropdownKey(config) {
    const {
        input,                  // block input containing the dropdowns
        jsonStructure,          // the JSON tree structure used for the block
        afterRenderCallback,    // callback after rendering the changes for the block
        treatNullSchemaAsUnknownType
    } = config;

    const dropdownFields = getDropdownFields(input);

    if (dropdownFields.length > 0) {
        // remove the last dropdown from the block
        input.removeField(dropdownFields.length - 1);
        input.sourceBlock_.initSvg();
        input.sourceBlock_.render();
    }

    setTimeout(() => {
        setOutputShapeOfBlock({
            input: input,
            jsonStructure: jsonStructure,
            treatNullSchemaAsUnknownType
        });
        afterRenderCallback?.();
    });
}

// remove all dropdowns after the changed dropdown and update the block shape
export function updateDropdownsAfterChange(config) {
    const {
        input,                  // block input containing the dropdowns
        jsonStructure,          // the JSON tree structure used for the block
        changedDropdownIndex,   // the index of the dropdown that was changed, starting from 0 for the first dropdown
        treatNullSchemaAsUnknownType
    } = config;

    const dropdownFields = getDropdownFields(input);

    for (let i = changedDropdownIndex + 1; i < dropdownFields.length; i++) {
        input.removeField(i);
        input.sourceBlock_.initSvg();
        input.sourceBlock_.render();
    }

    setTimeout(() => {
        setOutputShapeOfBlock({
            input: input,
            jsonStructure: jsonStructure,
            treatNullSchemaAsUnknownType
        });
    });
}

// set the output shape of the block based on the selected values in the dropdowns
export function setOutputShapeOfBlock(config) {
    const {
        input,                  // block input containing the dropdowns
        jsonStructure,          // the JSON tree structure used for the block
        treatNullSchemaAsUnknownType = false // Preserve existing behavior unless a caller opts in for sanitized null schemas.
    } = config;

    const dropdownFields = getDropdownFields(input);

    // get the JSON structure at the last selected level
    let jsonStructureAtCurrentLevel = jsonStructure;
    for (let i = 0; i < dropdownFields.length; i++) {
        if (jsonStructureAtCurrentLevel === null || jsonStructureAtCurrentLevel === undefined) break;
        jsonStructureAtCurrentLevel = jsonStructureAtCurrentLevel[input.fieldRow.find(field => field.name === i).getValue()];
    }

    // A null schema value has no known Blockly type, so allow any output connection and use a round connector shape.
    if (treatNullSchemaAsUnknownType && jsonStructureAtCurrentLevel === null) {
        input.sourceBlock_.setOutput(true);
        input.sourceBlock_.setOutputShape(Blockly.OUTPUT_SHAPE_ROUND);
    } else if (typeof jsonStructureAtCurrentLevel === 'object') {
        // handle object and array types
        if (Array.isArray(jsonStructureAtCurrentLevel)) {
            input.sourceBlock_.setOutput(true, 'Array');
        } else {
            input.sourceBlock_.setOutput(true, 'Object');
        }
        input.sourceBlock_.setOutputShape(Blockly.OUTPUT_SHAPE_ROUND);
    } else {
        // handle non-object, non-array types
        switch (typeof(jsonStructureAtCurrentLevel)) {
            case 'boolean':
                input.sourceBlock_.setOutputShape(Blockly.OUTPUT_SHAPE_HEXAGONAL);
                input.sourceBlock_.setOutput(true, 'Boolean');
                break;
            case 'number':
                input.sourceBlock_.setOutputShape(Blockly.OUTPUT_SHAPE_ROUND);
                input.sourceBlock_.setOutput(true, 'Number');
                break;
            case 'string':
                input.sourceBlock_.setOutput(true, 'String');
                // fall through
            default:
                input.sourceBlock_.setOutputShape(Blockly.OUTPUT_SHAPE_SQUARE);
                break;
        }
    }

    // render block with new output shape
    input.sourceBlock_.initSvg();
    input.sourceBlock_.render();
}

// generates the output code for the selected path in the dropdowns
export function generateOutputPathCode(config) {
    const {
        baseExpression,         // base expression for the JSON object that we are trying to extract a value from
        dropdownFields,         // an array of the dropdown fields for generating the output code
        includeStructureComment // whether to include a structure comment indicating the selected path
    } = config;

    const structure = [];
    let path = baseExpression;
    let code = baseExpression;

    for (let field of dropdownFields) {
        const val = field.getValue();
        path += `['${val}']`;
        code += ` && ${path}`;
        structure.push(val);
    }

    let result = (baseExpression && baseExpression !== '()') ? `((${code}) ?? "")` : '""';
    if (includeStructureComment) {
        result = `/*STRUCTURE:${structure.join('|')}*/${result}`;
    }

    return [result, Blockly.JavaScript.ORDER_NONE];
}

// opens the Set Schema window to allow the user to get or paste a sample JSON object and set the JSON structure for the block
export function openSetSchemaWindow(config) {
    const {
        blockRef,               // reference to the block that contains the Set Schema button
        includeQueryButton,     // whether to include a button to get a JSON response from a URL provided in the block
        includeCopyButton,      // whether to include a button to copy the JSON schema in the window to the clipboard
        requestDetails = null,  // the details of the request to be made if the query button is clicked
        validationMsg = null,   // warning message to display for block after block input validation
        inputSizeLimit = null,  // maximum allowed size of the input JSON object (in bytes)
        initialSchema = undefined, // existing schema to display; omitted when creating a new schema
        applyHandler = () => {} // callback for when the Apply button is clicked
    } = config;

    Blockly.WidgetDiv.hide();
    Blockly.WidgetDiv.show(blockRef);

    const closeOrApplyHandler = (responseData, shouldApply = false) => { // Close uses false by default; only Apply passes true.
        Blockly.WidgetDiv.hide();
        $('.custom-rest-output').hide();

        if (shouldApply) {
            applyHandler(responseData);
        }
    }

    _getSetSchemaDom({
        blockRef: blockRef,
        includeQueryButton: includeQueryButton,
        includeCopyButton: includeCopyButton,
        requestDetails: requestDetails,
        closeOrApplyHandler: closeOrApplyHandler,
        validationMsg: validationMsg,
        inputSizeLimit: inputSizeLimit,
        initialSchema: initialSchema
    });

    Blockly.WidgetDiv.hide();
}

// helper for generating the Set Schema window DOM and setting the behaviour of the buttons
function _getSetSchemaDom(config) {
    const DELAY_BEFORE_REENABLING_COPY_BUTTON = 3000;       // 3 seconds

    const {
        blockRef,               // reference to the block that contains the Set Schema button
        includeQueryButton,     // whether to include a button to get a JSON response from a URL provided in the block
        includeCopyButton,      // whether to include a button to copy the JSON schema in the window to the clipboard
        requestDetails,         // the details of the request to be made if the query button is clicked
        closeOrApplyHandler,    // callback for when the Close or Apply button is clicked
        validationMsg,          // warning message to display for block after block input validation
        inputSizeLimit,         // maximum allowed size of the input JSON object (in bytes) - nullable
        initialSchema           // existing schema used to prefill the dialog; undefined for a new schema
    } = config;

    // create artificial output container for Set Schema window
    $('.custom-rest-output').remove();
    const holder = $('.blocklyWidgetDiv');
    const restOutputContainer = $(`
        <div class='custom-rest-output'>
            <div class='rest-service-test'> 
                <div class='responseUrl'></div>
                <textarea class='responseArea' placeholder='Paste Schema'></textarea>
            <div class='rest-control'>
                <button class='rest-close' type='button' value='close'>Close</button>
                <button class='rest-done disabled' type='button' value='apply' disabled>Apply</button>
                ${includeQueryButton ? `<button class='rest-queryurl' type='button' value='queryurl'>Get RESPONSE FROM URL</button>` : ''}
                ${includeCopyButton ? `<button class='rest-copy' type='button' value='copy'><i></i><span>Copy</span></button>` : ''}
            </div>
        </div>`);
    holder.after(restOutputContainer);

    // get DOM elements from Set Schema window
    const responseArea = restOutputContainer.find('.responseArea');
    const containerTitle = restOutputContainer.find('.responseUrl');
    const closeButton = restOutputContainer.find('.rest-close');
    const applyButton = restOutputContainer.find('.rest-done');
    const queryUrlButton = includeQueryButton ? restOutputContainer.find('.rest-queryurl') : null;
    const copyButton = includeCopyButton ? restOutputContainer.find('.rest-copy') : null;

    // define messages for the title bar of the Set Schema window
    const defaultTitle = 'Input a sample JSON or XML response here to set the schema.';
    const invalidJsonErrorMsg = 'Invalid JSON/XML. Please verify the input schema.';
    const sizeLimitExceededErrorMsg = inputSizeLimit ? `Input exceeds maximum allowed size of ${_convertSizeInBytesToReadableString(inputSizeLimit)}.` : '';

    let schema = null;          // raw input from Set Schema window textbox
    let responseData = null;    // parsed JSON object (or stringified version) from raw input

    if (initialSchema !== undefined) {
        schema = JSON.stringify(initialSchema, null, 2);
        responseData = initialSchema;
        responseArea.val(schema);
        applyButton.removeAttr('disabled').removeClass('disabled');
    }

    // resize Set Schema window according to Blockly zoom level
    Util.resizeTestDom(blockRef.workspace.scale);

    // set title text
    containerTitle.text(defaultTitle);

    // "on change" handler for textbox of Set Schema window
    responseArea.on('input', function () {
        schema = responseArea.val().trim();
        if (schema === '') {
            applyButton.attr('disabled', 'disabled').addClass('disabled');
            return;
        }

        try {
            if (inputSizeLimit && schema.length > inputSizeLimit) {
                throw new Error(sizeLimitExceededErrorMsg);
            }
            try {
                responseData = JSON.parse(schema);
            } catch (jsonErr) {
                if (looksLikeXml(schema)) {
                    responseData = xmlToJson(schema);
                } else {
                    throw jsonErr;
                }
            }
            applyButton.removeAttr('disabled').removeClass('disabled');
            containerTitle.text(defaultTitle);
        } catch (e) {
            applyButton.attr('disabled', 'disabled').addClass('disabled');
            containerTitle.text(e.message === sizeLimitExceededErrorMsg ? sizeLimitExceededErrorMsg : invalidJsonErrorMsg);
        }
    });

    // handler for Close button
    closeButton.on('click', () => closeOrApplyHandler());

    // handler for Apply button
    applyButton.on('click', () => closeOrApplyHandler(responseData, true));

    // handler for Copy button
    includeCopyButton && copyButton.on('click', () => {
        copyButton.addClass('disabled');
        _copyToClipboard(responseArea.val(), function (result, err) {
            copyButton.find('span').text('Copied');

            // after the delay expires, reset the Copy button to its enabled status
            setTimeout(function () {
                copyButton.removeClass('disabled');
                copyButton.find('span').text('Copy');
            }, DELAY_BEFORE_REENABLING_COPY_BUTTON);
        });
    });

    // handler for Get Response from URL button
    includeQueryButton && queryUrlButton.on('click', () => {
        _queryUrlButtonHandler(containerTitle, responseArea, applyButton, queryUrlButton, requestDetails, validationMsg)
            .then(data => {
                responseData = data;
            });
        }
    );
}

// helper for copying input text to clipboard
function _copyToClipboard(textToCopy, callback) {
    const _callbackFunction = function (result, err) {
        callback?.(result, err);
    }
    
    // old way (execCommand copy)
    const _fallback = function () {
        // create dummy textarea element
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style = 'top: 0px; left: 0px; position: fixed; opacity: 0;'; // avoid scrolling to bottom
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            // execCommand is deprecated, but it's kept here intentionally as the only fallback
            // for browsers/contexts (e.g. non-HTTPS) that don't support navigator.clipboard.
            const successful = document.execCommand('copy'); // NOSONAR
            if (!successful) {
                console.error('Could not copy text (execCommand): no error');
            }
            _callbackFunction(successful);
        } catch (err) {
            console.error('Could not copy text (execCommand): ', err);
            _callbackFunction(false, err);
        }

        // cleanup (remove dummy textarea element)
        textArea.remove();
    }
    
    // modern way (async clipboard api)
    if (navigator.clipboard) {
        navigator.clipboard.writeText(textToCopy).then(function () {
            _callbackFunction(true);
        }, function (err) {
            // either it is http or no permission was granted
            console.error('Could not copy text (clipboard api): ', err);
            _fallback();
        });
        return;
    }
    
    // fallback to old way
    _fallback();
}

// handler for Get Response from URL button
function _queryUrlButtonHandler(containerTitle, responseArea, applyButton, queryUrlButton, requestDetails, validationMsg) {
    return new Promise((resolve, reject) => {
        if (!requestDetails) {
            if (validationMsg) responseArea.val(validationMsg);
            return resolve(null); // error is already displayed in the Set Schema window - no further error handling needed
        }

        const requestData = JSON.parse(requestDetails.data);
        const url = requestData.requestUrl;
        const method = requestData.method;
        containerTitle.text(`URL: ${method} ${url}`);

        // disable the button while request is running
        queryUrlButton.attr('disabled', true);

        // Send the request through the parent window (Snap) via postRobot
        // Routes through Snap's BlocklyController with ConsoleAuthorize
        postRobot.send(window.parent, 'blocklyProxyRequest', requestData)
            .then((response) => {
                // postRobot succeeded - check if HTTP call was successful
                if (response.data.success) {
                    // legacy backends may return a raw XML string instead of a JSON body -
                    // convert it to the same plain object shape a JSON response would produce
                    let responseValue = response.data.data;
                    if (typeof responseValue === 'string' && looksLikeXml(responseValue)) {
                        responseValue = xmlToJson(responseValue);
                    }
                    responseArea.val(JSON.stringify(responseValue, null, 2));
                    applyButton.removeAttr('disabled').removeClass('disabled');
                    queryUrlButton.removeAttr('disabled');
                    resolve(responseValue);
                } else {
                    // HTTP call failed (4xx/5xx from backend)
                    let errStr = 'An error occurred: ' + (response.data.statusCode || 'Unknown');
                    if (response.data.error) {
                        errStr += ' - ' + (typeof response.data.error === 'string' ? response.data.error : JSON.stringify(response.data.error));
                    }
                    responseArea.val(errStr);
                    queryUrlButton.removeAttr('disabled');
                    resolve(null);
                }
            })
            .catch((err) => {
                // postRobot communication failed (e.g., parent window closed, cross-origin blocked)
                let errStr = 'An error occurred';
                if (err.message) {
                    errStr += ': ' + err.message;
                }
                responseArea.val(errStr);
                queryUrlButton.removeAttr('disabled');
                resolve(null);
            });
    });
}

// helper for converting an integer number of bytes into a readable string (supports MB, KB, and bytes)
function _convertSizeInBytesToReadableString(bytes) {
    const KB = 1024;
    const MB = KB * 1024;

    if (bytes >= MB) {
        const mb = bytes / MB;
        return `${Math.floor(mb * 10) / 10} MB`;
    } else if (bytes >= KB) {
        const kb = bytes / KB;
        return `${Math.floor(kb * 10) / 10} KB`;
    } else {
        return `${bytes} bytes`;
    }
}
